import { Box, Button, Card, Tabs, TextInput, Title } from '@mantine/core';
import { IconBolt, IconCalendar, IconCalendarPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import MarketingChannelsFlowCampaignCards from './marketingChannelsFlowCampaignCards';
import MarketingChannelsFlowWorkflowCards from './marketingChannelsFlowWorkflowCards';

const MarketingChannelsFlow = ({ elementTypes, element, type }) => {
  const [guidanceTab, setGuidanceTab] = useState(elementTypes.length === 1 ? elementTypes[0] : null);
  const [campaignsFlowTabs, setCampaignsFlowTabs] = useState(null);
  const [searchTerms, setSearchTerms] = useState('');
  const navigate = useNavigate();
  const { accountId, domainId } = useParams();

  const handleGuidanceChange = (key) => {
    setSearchTerms('');
    setGuidanceTab(key);
  };

  const handleCampaignsTabsChange = (key) => {
    setCampaignsFlowTabs(key);
  };

  // Continuous/Triggered availability per channel. Text channels: both. Ad channels
  // (Google/Facebook): workflows only — except Google Ad campaigns, which now also support
  // Continuous (the audience is the auto-populated rule-based user list; the behavior/GA4 path is
  // gated on a behavior-scope segment and is unaffected). These depend only on `type`, so they're
  // computed once here rather than per `elementType` iteration below.
  const isTextChannel = [
    'email',
    'Viber',
    'SMS / Text Message',
    'Push Notification',
    'Whatsapp Message',
    'Webhook',
  ].includes(type);
  // Google Ads are continuous-only — for both campaigns and workflows. No one-off, no triggered.
  // Facebook Ad keeps its previous behavior (one-off + workflow continuous/triggered).
  const isGoogleAd = type === 'Google Ad';
  const showOneOff = !isGoogleAd;

  return (
    <>
      <Tabs className="guidanceTabs innerNav" mb={0} value={guidanceTab} onChange={(key) => handleGuidanceChange(key)}>
        <Tabs.List>
          {elementTypes.map((elementType) => {
            const singular = elementType.slice(0, elementType.length - 1);
            const singularCapitalized = singular[0].toUpperCase() + singular.slice(1, elementType.length - 1);
            return (
              <Tabs.Tab key={elementType} value={elementType}>
                {singularCapitalized}
              </Tabs.Tab>
            );
          })}
        </Tabs.List>
        {elementTypes.map((elementType) => {
          const singular = elementType.slice(0, elementType.length - 1);
          const singularCapitalized = singular[0].toUpperCase() + singular.slice(1, elementType.length - 1);
          // Only the Facebook-workflow check depends on `elementType`; the `type`-only values
          // (isTextChannel, isGoogleAd, showOneOff) are hoisted above the map.
          const isFacebookWorkflow = type === 'Facebook Ad' && elementType === 'workflows';
          const showContinuous = isTextChannel || isFacebookWorkflow || isGoogleAd;
          const showTriggered = isTextChannel || isFacebookWorkflow;
          return (
            <Tabs.Panel key={elementType} value={elementType}>
              <Card p="sm" withBorder>
                <Tabs
                  className="guidanceTabs innerNav"
                  mb={0}
                  value={campaignsFlowTabs}
                  onChange={(key) => handleCampaignsTabsChange(key)}
                >
                  <Tabs.List>
                    <Tabs.Tab value="listCampaigns">{`Use Existing ${singularCapitalized}`}</Tabs.Tab>
                    <Tabs.Tab value="newCampaign">{`Create New ${singularCapitalized}`}</Tabs.Tab>
                  </Tabs.List>
                  <Tabs.Panel value="listCampaigns">
                    <Box display="flex" ml={10} mr={10} style={{ marginBottom: 'var(--mantine-spacing-sm)' }}>
                      <TextInput
                        onChange={(e) => setSearchTerms(e.target.value)}
                        type="search"
                        placeholder={`Filter ${elementType}...`}
                        aria-label="Search"
                        value={searchTerms}
                      />
                    </Box>

                    {elementType === 'campaigns' && (
                      <MarketingChannelsFlowCampaignCards searchTerms={searchTerms} element={element} type={type} />
                    )}
                    {elementType === 'workflows' && (
                      <MarketingChannelsFlowWorkflowCards searchTerms={searchTerms} element={element} type={type} />
                    )}
                  </Tabs.Panel>
                  <Tabs.Panel value="newCampaign">
                    <Title order={6} ta="center">What type of {singular} do you want to create:</Title>
                    <Box ta="center">
                      {showOneOff && (
                        <Button
                          variant="default"
                          mr="sm"
                          onClick={(redirect) => {
                            redirect &&
                              navigate(
                                `/accounts/${accountId}/domains/${domainId}/${elementType}/new?mode=oneOff&flowType=${type}&flowElementId=${
                                  element.id
                                }&flowElementName=${encodeURIComponent(element.name)}`
                              );
                          }}
                        >
                          <IconCalendarPlus size={14} /> One Off {singularCapitalized}
                        </Button>
                      )}
                      {showContinuous && (
                        <Button
                          variant="default"
                          mr="sm"
                          onClick={(redirect) => {
                            redirect &&
                              navigate(
                                `/accounts/${accountId}/domains/${domainId}/${elementType}/new?mode=continuous&flowType=${type}&flowElementId=${
                                  element.id
                                }&flowElementName=${encodeURIComponent(element.name)}`
                              );
                          }}
                        >
                          <IconCalendar size={14} /> Continuous {singularCapitalized}
                        </Button>
                      )}
                      {showTriggered && (
                        <Button
                          variant="default"
                          onClick={(redirect) => {
                            redirect &&
                              navigate(
                                `/accounts/${accountId}/domains/${domainId}/${elementType}/new?mode=triggered&flowType=${type}&flowElementId=${
                                  element.id
                                }&flowElementName=${encodeURIComponent(element.name)}`
                              );
                          }}
                        >
                          <IconBolt size={14} /> Triggered {singularCapitalized}
                        </Button>
                      )}
                    </Box>
                  </Tabs.Panel>
                </Tabs>
              </Card>
            </Tabs.Panel>
          );
        })}
      </Tabs>
    </>
  );
};

export default MarketingChannelsFlow;
